// lib/marketClock.js — the frontend's one market clock.
//
// Five components carried their own copy of "weekday && 9:30–16:00 ET"
// (two closing at `<= 16:00`, two at `< 16:00`, one with a holiday set, the
// rest without) while every caller of /omega/market-status threw away the
// fields that know better (isHoliday, early closes, minsToClose). Here the
// local rule — weekday, 9:30 <= t < 16:00 ET, not an NYSE holiday — is the
// instant/offline answer, and the backend's status (polled every 60s once
// startMarketClock() runs) can only ever CLOSE the session (holiday, early
// close). It never opens one the local rule says is closed, so a poll that
// is a minute stale across the 16:00 bell can't show "open" after it.
import { fastGetAsync } from "./fastFetch";

// Same list as backend/market_clock.py's _US_MARKET_HOLIDAYS and the
// dependency-free FOUC-guard copy in public/index.html (that one runs
// before any bundle loads, so it cannot import this). Update together.
export const US_MARKET_HOLIDAYS = new Set([
  "2025-01-01", "2025-01-20", "2025-02-17", "2025-04-18",
  "2025-05-26", "2025-06-19", "2025-07-04", "2025-09-01",
  "2025-11-27", "2025-12-25",
  "2026-01-01", "2026-01-19", "2026-02-16", "2026-04-03",
  "2026-05-25", "2026-06-19", "2026-07-03", "2026-09-07",
  "2026-11-26", "2026-12-25",
]);

const OPEN_MIN = 9 * 60 + 30;   // 09:30 ET
const CLOSE_MIN = 16 * 60;      // 16:00 ET — closed from the bell itself

export function etNow(now = new Date()) {
  return new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
}

export function etDateStr(et) {
  return `${et.getFullYear()}-${String(et.getMonth() + 1).padStart(2, "0")}-${String(et.getDate()).padStart(2, "0")}`;
}

/** The local rule alone: weekday, 09:30 <= t < 16:00 ET, not an NYSE holiday. */
export function localEquityOpen(now = new Date()) {
  try {
    const et = etNow(now);
    const day = et.getDay(); // 0=Sun, 6=Sat
    if (day === 0 || day === 6) return false;
    if (US_MARKET_HOLIDAYS.has(etDateStr(et))) return false;
    const mins = et.getHours() * 60 + et.getMinutes();
    return mins >= OPEN_MIN && mins < CLOSE_MIN;
  } catch {
    return false;
  }
}

// ── Server-fed refinement ──────────────────────────────────────────────────
const SERVER_FRESH_MS = 120 * 1000;
let _server = null;   // last /omega/market-status payload
let _serverAt = 0;
let _timer = null;
const _listeners = new Set();

export function serverStatus() {
  return _server && Date.now() - _serverAt < SERVER_FRESH_MS ? _server : null;
}

/** Equity session open right now. The server can close it; only the local rule opens it. */
export function isEquityOpen(now = new Date()) {
  const local = localEquityOpen(now);
  if (!local) return false;
  const s = serverStatus();
  if (s && s.isOpen === false) return false;   // holiday / early close the local rule can't know
  return true;
}

export function marketStatus() {
  const s = serverStatus();
  const local = localEquityOpen();
  return {
    isOpen: isEquityOpen(),
    session: s?.session ?? (local ? "open" : "closed"),
    equitySession: s?.equitySession ?? null,
    minsToClose: s?.minsToClose ?? null,
    minsToOpen: s?.minsToOpen ?? null,
    isHoliday: s?.isHoliday ?? US_MARKET_HOLIDAYS.has(etDateStr(etNow())),
    etTime: s?.etTime ?? null,
    source: s ? "server" : "local",
  };
}

export function onMarketClock(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

function _notify() {
  const status = marketStatus();
  for (const fn of _listeners) {
    try { fn(status); } catch {}
  }
}

/** Start the 60s poll of /omega/market-status. Idempotent; returns a stop function for the owner. */
export function startMarketClock(apiBase, intervalMs = 60 * 1000) {
  if (_timer) return () => {};
  const tick = async () => {
    try {
      const r = await fastGetAsync(`${apiBase}/omega/market-status`);
      if (r?.data && typeof r.data === "object") {
        _server = r.data;
        _serverAt = Date.now();
        _notify();
      }
    } catch {}
  };
  tick();
  _timer = setInterval(tick, intervalMs);
  return () => { clearInterval(_timer); _timer = null; };
}

/** "auto" theme = light while the equity session is open, dark otherwise. */
export function resolveThemeChoice(choice) {
  if (choice !== "auto") return choice === "light" ? "light" : "dark";
  try { return isEquityOpen() ? "light" : "dark"; } catch { return "dark"; }
}

export function _setServerStatusForTests(payload, at = Date.now()) {
  _server = payload;
  _serverAt = payload ? at : 0;
}
